import {
  setDivision,
  setDivisions,
  setDivisionSearchResults,
  setDivisionsForOrganisation,
} from "reduxStore/state";

const BASE_URL = process.env.REACT_APP_BASE_URL;

export const createDivision = (values) => async (dispatch, getState) => {
  try {
    const token = getState().token;

    const createdDivisionResponse = await fetch(`${BASE_URL}/divisions`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        ...values,
        locations: values.locations, // This ensures you're sending an array of locations
      }),
    });

    if (!createdDivisionResponse.ok) {
      throw new Error("Failed to create division");
    }

    const createdDivision = await createdDivisionResponse.json();

    dispatch(setDivision(createdDivision));
    dispatch(
      fetchDivisions({
        page: 1,
        pageSize: 10,
        sort: "",
        search: "",
        location: "",
      })
    );

    return createdDivision;
  } catch (error) {
    console.error("Error while creating division:", error);
    return null;
  }
};

export const fetchDivisionForOrganisation =
  ({ organisationId, locationId }) =>
  async (dispatch, getState) => {
    try {
      const token = getState().token;

      const queryParams = new URLSearchParams({
        ...(organisationId && { organisationId }),
        ...(locationId && { locationId }),
      }).toString();

      const response = await fetch(
        `${BASE_URL}/divisions/organisation?${queryParams}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch divisions by location");
      }

      const data = await response.json();

      dispatch(setDivisionsForOrganisation(data));

      return data;
    } catch (error) {
      console.error("Error while fetching divisions by location:", error);
      return null;
    }
  };

export const updateDivision =
  (divisionId, values) => async (dispatch, getState) => {
    try {
      const token = getState().token;

      const updatedDivisionResponse = await fetch(
        `${BASE_URL}/divisions/${divisionId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            ...values,
            locations: values.locations, // Ensures that updated locations are sent as an array
          }),
        }
      );

      if (!updatedDivisionResponse.ok) {
        throw new Error("Failed to update division");
      }

      const updatedDivision = await updatedDivisionResponse.json();

      dispatch(setDivision(updatedDivision));
      dispatch(
        fetchDivisions({
          page: 1,
          pageSize: 10,
          sort: "",
          search: "",
          location: "",
        })
      );

      return updatedDivision;
    } catch (error) {
      console.error("Error while updating division:", error);
      return null;
    }
  };

export const fetchDivisions =
  ({
    page = 1,
    pageSize = 10,
    sort = "createdAt",
    order = "desc",
    search,
    location,
  } = {}) =>
  async (dispatch, getState) => {
    try {
      const token = getState().token;

      const query = new URLSearchParams({
        page: Number(page), // Convert to number
        limit: Number(pageSize), // Use 'limit' instead of 'pageSize'
        sort,
        order,
        search,
        location,
      }).toString();

      const response = await fetch(`${BASE_URL}/divisions?${query}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch divisions");
      }

      const data = await response.json();

      dispatch(setDivisions({ data: data.data, total: data.total }));

      return data;
    } catch (error) {
      console.error("Error while fetching divisions:", error);
      return null;
    }
  };

export const fetchDivisionById = (divisionId) => async (dispatch, getState) => {
  try {
    const token = getState().token;

    const divisionResponse = await fetch(
      `${BASE_URL}/divisions/${divisionId}`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }
    );

    if (!divisionResponse.ok) {
      throw new Error("Failed to fetch division");
    }

    const division = await divisionResponse.json();

    dispatch(setDivision(division)); // Ensure this sets the division with the populated locations array

    return division;
  } catch (error) {
    console.error("Error while fetching division:", error);
    return null;
  }
};

export const searchDivisions =
  ({ search, location = null } = {}) =>
  async (dispatch, getState) => {
    try {
      const token = getState().token;

      const query = new URLSearchParams({
        search,
        location,
      }).toString();

      const searchResponse = await fetch(
        `${BASE_URL}/divisions/search?${query}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!searchResponse.ok) {
        throw new Error("Failed to search divisions");
      }

      const data = await searchResponse.json();

      dispatch(setDivisionSearchResults(data));

      return data;
    } catch (error) {
      console.error("Error while searching divisions:", error);
      return null;
    }
  };

export const deleteDivision = (divisionId) => async (dispatch, getState) => {
  try {
    const token = getState().token;

    const deleteResponse = await fetch(`${BASE_URL}/divisions/${divisionId}`, {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    const responseData = await deleteResponse.json();

    if (!deleteResponse.ok || !responseData) {
      throw new Error(responseData.message);
    }

    dispatch(
      fetchDivisions({
        page: 1,
        pageSize: 10,
        sort: "",
        search: "",
        location: "",
      })
    );

    return { success: true, message: responseData.message };
  } catch (error) {
    console.error("Cannot delete Department with associated users:", error);

    // Detect if the error is due to associated assessments
    if (
      error.message === "Cannot delete Department with associated assessments"
    ) {
      return { error: error.message, associatedAssessments: true };
    }

    return {
      error: true,
      success: false,
      message: error.message,
    };
  }
};

// Reassign assessments from one division to another
export const reassignAssessments =
  (oldDivisionId, newDivisionId) => async (dispatch, getState) => {
    try {
      const token = getState().token;

      const response = await fetch(
        `${BASE_URL}/divisions/reassign-assessments`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ oldDivisionId, newDivisionId }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to reassign assessments");
      }

      const data = await response.json();
      return { success: true, message: data.message };
    } catch (error) {
      console.error("Error while reassigning assessments:", error);
      return { error: true, message: error.message };
    }
  };
